import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Modal, Form, Input, Select, DatePicker, Switch, notification } from 'antd';
import { useNotificationsLogic } from '../useNotificationsLogic';
import { title } from '../../validation/regex';
import { useI18nLocalization } from '../../i18nLocalization/useI18nLocalization';

const EditNotificationModal = ({ isVisible, onSaveCallback, onCloseCallback, ntificn }) => {
	dayjs.extend(utc);

	const notificationsLogic = useNotificationsLogic();

	const [form] = Form.useForm();
	const [isSaving, setIsSaving] = useState(false);
	const [areFormErrors, setAreFormErrors] = useState(false);

	const { t, getDateFormat, getAntLocale } = useI18nLocalization();

	const [notificationTypes, setNotificationTypes] = useState(null);

	useEffect(() => {
		if (!notificationTypes) {
			getNotificationTypes();
		}
	}, [JSON.stringify(notificationTypes)]);

	const getNotificationTypes = async () => {
		const nts = await notificationsLogic.getNotificationTypes();
		setNotificationTypes(nts);
	};

	useEffect(() => {
		form.setFieldsValue({
			enabled: ntificn?.enabled,
			notificationType: ntificn?.notificationType,
			displayType: ntificn?.banner,
			title: ntificn?.title,
			message: ntificn?.message,
			notifyThroughDateUtc: dayjs(ntificn?.notifyThroughDateUtc),
			dismissable: ntificn?.dismissable
		});
	}, [JSON.stringify(ntificn)]);

	const getUpdatedNotification = (formValues) => {
		const updatedNotificationObj = {
			enabled: formValues.enabled,
			notificationType: formValues.notificationType,
			banner: formValues.displayType,
			oldTitle: ntificn.title,
			title: formValues.title,
			message: formValues.message,
			oldNotifyThroughDateUtc: ntificn.notifyThroughDateUtc,
			notifyThroughDateUtc: formValues.notifyThroughDateUtc.utc().format(),
			dismissable: formValues.dismissable ? true : false
		};
		return updatedNotificationObj;
	};

	const handleOnSave = async (formValues) => {
		setIsSaving(true);
		const updatedNotification = getUpdatedNotification(formValues);
		const successful = await notificationsLogic.updateNotification(updatedNotification);
		if (successful) {
			notification['success']({
				message: `${t(`NotificationSaved`)}!`
			});

			clearForm();
			onSaveCallback();
		} else {
			notification['error']({
				message: t('UpdateNotificationError'),
				duration: 10
			});
		}

		setIsSaving(false);
	};

	const handleOnCancel = () => {
		setIsSaving(false);
		clearForm();
		onCloseCallback();
	};

	const clearForm = () => {
		form.resetFields();
	};

	return (
		<>
			<Modal
				title={t('EditNotification')}
				visible={isVisible}
				okText={t('Save')}
				onOk={() => {
					//-- Will trigger the form's onFinish event
					form.submit();
				}}
				okButtonProps={{ disabled: areFormErrors }}
				confirmLoading={isSaving}
				onCancel={handleOnCancel}
				cancelText={t('Cancel')}
				forceRender //-- Needed b/c of using form.setFieldsValue to set the value of the fields
			>
				<Form
					form={form}
					onFieldsChange={() => {
						const areErrors = form.getFieldsError().some((fe) => fe.errors.length > 0);
						setAreFormErrors(areErrors);
					}}
					layout='vertical'
					requiredMark
					scrollToFirstError
					onFinish={handleOnSave}
				>
					<Form.Item label={t('Enabled')} name='enabled' valuePropName='checked'>
						<Switch checkedChildren={t('Yes')} unCheckedChildren={t('No')} />
					</Form.Item>
					<Form.Item label={t('NotificationType')} name='notificationType' required>
						<Select id='notificationType' placeholder={t('ChooseNotificationType')}>
							{_.map(notificationTypes, (nt) => {
								return (
									<Select.Option key={nt.id} value={nt.type}>
										{nt.type} <span style={{ fontStyle: 'italic' }}>({nt.description})</span>
									</Select.Option>
								);
							})}
						</Select>
					</Form.Item>
					<Form.Item label={t('DisplayType')} name='displayType'>
						<Select id='displayType' placeholder={t('ChooseDisplayType')}>
							<Select.Option value={true}>{t('Banner')}</Select.Option>
							<Select.Option value={false}>{t('Toast')}</Select.Option>
						</Select>
					</Form.Item>
					<Form.Item
						label={t('Dismissable')}
						name='dismissable'
						valuePropName='checked'
						tooltip={t('NoEffectOnToastNotifications')}
					>
						<Switch checkedChildren={t('Yes')} unCheckedChildren={t('No')} />
					</Form.Item>
					<Form.Item
						label={t('NotifyThroughDate')}
						name='notifyThroughDateUtc'
						required
						rules={[
							{
								validator: (field, value) => {
									if (!value) {
										return Promise.reject(new Error(t('NotifyThroughDateRequired')));
									}

									return Promise.resolve();
								}
							}
						]}
					>
						<DatePicker locale={getAntLocale().DatePicker} format={getDateFormat()} />
					</Form.Item>
					<Form.Item
						label={t('Title')}
						name='title'
						required
						rules={[
							{
								validator: (field, value) => {
									if (!value) {
										return Promise.reject(new Error(t('Title is required')));
									}

									if (!value.match(title)) {
										return Promise.reject(new Error(`1-50 ${t('alpha and some special chars (inclusive)')}`));
									}

									return Promise.resolve();
								}
							}
						]}
					>
						<Input placeholder={t('title')} />
					</Form.Item>
					<Form.Item
						label={t('Message')}
						name='message'
						required
						rules={[
							{
								validator: (field, value) => {
									if (!value) {
										return Promise.reject(new Error(t('MessageRequired')));
									}

									return Promise.resolve();
								}
							}
						]}
					>
						<Input.TextArea rows={10} placeholder={t('message')} style={{ whiteSpace: 'pre-wrap' }} />
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

EditNotificationModal.propTypes = {
	isVisible: PropTypes.bool.isRequired,
	onSaveCallback: PropTypes.func.isRequired,
	onCloseCallback: PropTypes.func.isRequired,
	ntificn: PropTypes.object
};

export default EditNotificationModal;
